import { useState, useEffect } from "react";
import { COMMON_TIMEZONES, DEFAULT_TIMEZONE } from "../constants/timezones";

const applyDefaults = (data) => ({
  timezone: DEFAULT_TIMEZONE,
  use_for_forecasting: true,
  is_tracker: false,
  backtracking_enabled: true,
  tracker_stow_position_deg: null,
  tracker_stow_wind_speed_mps: null,
  tracker_resume_wind_speed_mps: null,
  ...(data || {}),
});

const parseNumber = (value) => (value === "" ? null : parseFloat(value));

export default function SiteForm({ title, onClose, onSubmit, initialValues }) {
  const [values, setValues] = useState(applyDefaults(initialValues));
  useEffect(() => {
    setValues(applyDefaults(initialValues));
  }, [initialValues]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSubmit(values);
  };
  const showTrackerFields = !!values.is_tracker;

  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-xl">
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Site name</label>
                <input
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={values.name || ""}
                  onChange={(e) => set("name", e.target.value)}
                  required
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Address</label>
                <input
                  className="w-full border rounded-md px-3 py-2"
                  value={values.address || ""}
                  onChange={(e) => set("address", e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  className="w-full border rounded-md px-3 py-2"
                  value={values.city || ""}
                  onChange={(e) => set("city", e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">ZIP code</label>
                <input
                  className="w-full border rounded-md px-3 py-2"
                  value={values.zip_code || ""}
                  onChange={(e) => set("zip_code", e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <input
                  className="w-full border rounded-md px-3 py-2"
                  value={values.country || ""}
                  onChange={(e) => set("country", e.target.value)}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Timezone</label>
                <select
                  className="w-full border rounded-md px-3 py-2"
                  value={values.timezone || DEFAULT_TIMEZONE}
                  onChange={(e) => set("timezone", e.target.value)}
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">GPS Latitude (°)</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-md px-3 py-2"
                  value={values.gps_latitude ?? ""}
                  onChange={(e) => set("gps_latitude", parseNumber(e.target.value))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">GPS Longitude (°)</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-md px-3 py-2"
                  value={values.gps_longitude ?? ""}
                  onChange={(e) => set("gps_longitude", parseNumber(e.target.value))}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Cable losses (W)</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-md px-3 py-2"
                  value={values.cable_losses_site ?? ""}
                  onChange={(e) => set("cable_losses_site", parseNumber(e.target.value))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Min irradiance for availability (W/m^2)</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-md px-3 py-2"
                  value={values.irrad_avail ?? ""}
                  onChange={(e) => set("irrad_avail", parseNumber(e.target.value))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Min irradiance for PR (W/m^2)</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-md px-3 py-2"
                  value={values.irrad_pr ?? ""}
                  onChange={(e) => set("irrad_pr", parseNumber(e.target.value))}
                />
              </div>

              <div className="col-span-2 mt-2 border-t pt-4">
                <div className="text-sm font-semibold text-slate-800">Forecasting</div>
                <p className="mt-1 text-xs text-slate-500">
                  Control whether this site participates in forecast generation and model training.
                </p>
              </div>

              <label className="col-span-2 flex items-center gap-3 rounded-md border px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!values.use_for_forecasting}
                  onChange={(e) => set("use_for_forecasting", e.target.checked)}
                />
                <span className="text-sm">Use for forecasting model</span>
              </label>

              <div className="col-span-2 mt-2 border-t pt-4">
                <div className="text-sm font-semibold text-slate-800">Mounting Geometry</div>
                <p className="mt-1 text-xs text-slate-500">
                  Fixed-tilt sites use tilt and surface azimuth. Tracker sites use default geometry that arrays can override.
                </p>
              </div>

              <label className="col-span-2 flex items-center gap-3 rounded-md border px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!values.is_tracker}
                  onChange={(e) => set("is_tracker", e.target.checked)}
                />
                <span className="text-sm">Tracker site</span>
              </label>

              {showTrackerFields ? (
                <>
                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Spacing between tracker rows in meters."
                    >
                      Default row pitch (m)
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.default_row_pitch_m ?? ""}
                      onChange={(e) => set("default_row_pitch_m", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Default spacing between tracker rows for arrays without overrides.</p>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Effective collector width perpendicular to the tracker axis, in meters."
                    >
                      Default collector width (m)
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.default_collector_width_m ?? ""}
                      onChange={(e) => set("default_collector_width_m", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Default effective collector width perpendicular to the tracker axis, in meters. Example: `2.384`, not `2384`.</p>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Axis orientation in degrees. Use 180 for a north-south axis to match the current tracker test-code sign convention."
                    >
                      Tracker axis azimuth (deg)
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.tracker_axis_azimuth_deg ?? ""}
                      onChange={(e) => set("tracker_axis_azimuth_deg", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Use `180` for a north-south axis in the current tracker test convention. `90` means east-west.</p>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Maximum allowed tracker rotation angle in degrees."
                    >
                      Max tracker angle (deg)
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.max_tracker_angle_deg ?? ""}
                      onChange={(e) => set("max_tracker_angle_deg", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Maximum allowed tracker rotation.</p>
                  </div>

                  <label className="col-span-2 flex items-center gap-3 rounded-md border px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!values.backtracking_enabled}
                      onChange={(e) => set("backtracking_enabled", e.target.checked)}
                    />
                    <span className="text-sm">Backtracking enabled</span>
                  </label>

                  <div className="col-span-2 mt-2 border-t pt-4">
                    <div className="text-sm font-semibold text-slate-800">Tracker Wind Stow</div>
                    <p className="mt-1 text-xs text-slate-500">
                      Configure the safe stow angle and wind thresholds used to interpret possible wind-stow behavior.
                    </p>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Safe tracker angle used during high-wind stow mode. Signed values are allowed if required by the tracker vendor."
                    >
                      Stow position (deg)
                    </label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.tracker_stow_position_deg ?? ""}
                      onChange={(e) => set("tracker_stow_position_deg", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Tracker safe angle used during wind stow, for example `0` for horizontal stow.</p>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Wind-speed threshold in meters per second above which the tracker should enter stow."
                    >
                      Stow wind speed (m/s)
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.tracker_stow_wind_speed_mps ?? ""}
                      onChange={(e) => set("tracker_stow_wind_speed_mps", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Entry threshold for high-wind stow mode.</p>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-1"
                      title="Lower wind-speed threshold used for hysteresis before normal tracking resumes."
                    >
                      Resume wind speed (m/s)
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.tracker_resume_wind_speed_mps ?? ""}
                      onChange={(e) => set("tracker_resume_wind_speed_mps", parseNumber(e.target.value))}
                    />
                    <p className="mt-1 text-xs text-slate-500">Optional lower threshold used for safe return to tracking.</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Fixed tilt angle (deg)</label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.fixed_tilt_deg ?? ""}
                      onChange={(e) => set("fixed_tilt_deg", parseNumber(e.target.value))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Surface azimuth (deg)</label>
                    <input
                      type="number"
                      step="any"
                      className="w-full border rounded-md px-3 py-2"
                      value={values.surface_azimuth_deg ?? ""}
                      onChange={(e) => set("surface_azimuth_deg", parseNumber(e.target.value))}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">AC nominal (MVA)</label>
                <input
                  className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
                  value={values.ac_nominal_capacity || ""}
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">DC nominal (MWp)</label>
                <input
                  className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-600 cursor-not-allowed"
                  value={values.dc_nominal_capacity || ""}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t bg-white px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
